import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import supabase from '../supabase';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import '../design-system.css';
import { useSiteLang as useLang } from '../siteLang';

const T = {
    fr: {
        eyebrow: 'winston.ie',
        title: 'Communiqués',
        subtitle: 'Archive des communiqués officiels de winston.ie.',
        empty: 'Aucun communiqué pour le moment.',
        view: 'Voir →',
        pdf: 'PDF',
        archived: '(archivé)',
        addressee: "À l'attention de : ",
        subject: 'Objet : ',
        label: 'a Longden Collective label',
        footer: 'winston.ie — communiqué officiel',
    },
    en: {
        eyebrow: 'winston.ie',
        title: 'Announcements',
        subtitle: "Archive of winston.ie's official announcements.",
        empty: 'No announcements yet.',
        view: 'View →',
        pdf: 'PDF',
        archived: '(archived)',
        addressee: 'Attention: ',
        subject: 'Subject: ',
        label: 'a Longden Collective label',
        footer: 'winston.ie — official communiqué',
    },
    vi: {
        eyebrow: 'winston.ie',
        title: 'Thông báo',
        subtitle: 'Lưu trữ các thông báo chính thức của winston.ie.',
        empty: 'Chưa có thông báo nào.',
        view: 'Xem →',
        pdf: 'PDF',
        archived: '(lưu trữ)',
        addressee: 'Kính gửi: ',
        subject: 'Về việc: ',
        label: 'a Longden Collective label',
        footer: 'winston.ie — thông báo chính thức',
    },
};

const TYPE_LABELS = {
    release: { fr: 'Diffusion immédiate', en: 'Immediate release', vi: 'Phát hành ngay' },
    media: { fr: 'Communiqué de presse', en: 'Press release', vi: 'Thông cáo báo chí' },
    global: { fr: 'Diffusion globale', en: 'Global release', vi: 'Phát hành toàn cầu' },
    update: { fr: 'Mise à jour', en: 'Update', vi: 'Cập nhật' },
};

function Communiques() {
    const navigate = useNavigate();
    const { lang } = useLang();
    const t = T[lang] || T.fr;
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => { document.title = `${t.title} | winston.ie`; }, [t.title]);

    useEffect(() => {
        const fetchAll = async () => {
            const { data } = await supabase.from('announcements').select('*').order('date', { ascending: false });
            setItems(data || []);
            setLoading(false);
        };
        fetchAll();
    }, []);

    const formatDate = (a) => {
        const d = a?.date ? new Date(`${a.date}T00:00:00`) : a?.created_at ? new Date(a.created_at) : null;
        if (!d) return '';
        const locale = lang === 'en' ? 'en-US' : lang === 'vi' ? 'vi-VN' : 'fr-CA';
        return d.toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' });
    };

    return (
        <div className="w-page">
            <Navbar variant="light" />

            <div className="w-body" style={{ maxWidth: '760px' }}>
                <p className="w-eyebrow">{t.eyebrow}</p>
                <h1 className="w-title" style={{ marginBottom: '10px' }}>{t.title}</h1>
                <p className="w-subtitle" style={{ marginBottom: '48px' }}>{t.subtitle}</p>

                {loading ? null : items.length === 0 ? (
                    <p className="w-subtitle">{t.empty}</p>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '28px', paddingBottom: '40px' }}>
                        {items.map((item) => (
                            <div
                                key={item.id}
                                style={{
                                    background: '#fff', color: '#0e1013', border: '1px solid #d8d5cc',
                                    padding: 'clamp(24px, 4.5vw, 56px) clamp(20px, 5vw, 52px)',
                                    fontFamily: 'Helvetica Neue, Arial, sans-serif', boxSizing: 'border-box',
                                    opacity: item.enabled ? 1 : 0.75,
                                }}
                            >
                                {/* En-tête — logo à gauche, lieu et date à droite, comme un vrai papier à en-tête */}
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px', gap: '16px' }}>
                                    <img src="/winston-logo-word.svg" alt="winston.ie" style={{ height: '18px', width: 'auto' }} />
                                    <p style={{ fontSize: '11px', fontWeight: 500, color: '#6b6459', margin: 0, textAlign: 'right', whiteSpace: 'nowrap' }}>
                                        {item.location ? `${item.location}, le ` : 'Le '}{formatDate(item)}
                                        {!item.enabled && (
                                            <span style={{ marginLeft: '8px', color: '#a19c8f' }}>{t.archived}</span>
                                        )}
                                    </p>
                                </div>
                                <p style={{ fontSize: '9.5px', fontWeight: 500, color: '#a19c8f', margin: '0 0 22px', letterSpacing: '0.02em' }}>
                                    {t.label}
                                </p>

                                <div style={{ width: '100%', height: '1px', background: '#0e1013', marginBottom: '22px' }} />

                                {item.recipient && (
                                    <p style={{ fontSize: '13px', fontWeight: 500, color: '#3a3632', margin: '0 0 18px' }}>
                                        {t.addressee}<strong style={{ fontWeight: 700 }}>{item.recipient}</strong>
                                    </p>
                                )}

                                <p style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#6b6459', margin: '0 0 14px' }}>
                                    {TYPE_LABELS[item.type]?.[lang] || TYPE_LABELS[item.type]?.fr || item.type}
                                </p>

                                <h2 style={{ fontSize: 'clamp(17px, 2.2vw, 22px)', fontWeight: 700, color: '#0e1013', margin: '0 0 24px', lineHeight: 1.35 }}>
                                    {t.subject}{item.title}
                                </h2>

                                {item.greeting && (
                                    <p style={{ fontSize: '14px', fontWeight: 500, color: '#0e1013', margin: '0 0 16px' }}>
                                        {item.greeting}
                                    </p>
                                )}

                                {item.body && (
                                    <p style={{ fontSize: '14px', fontWeight: 500, color: '#3a3632', lineHeight: 1.85, margin: '0 0 28px', whiteSpace: 'pre-wrap' }}>
                                        {item.body}
                                    </p>
                                )}

                                {item.pdf_url && (
                                    <div style={{ marginBottom: '28px', overflow: 'hidden', border: '1px solid #e5e2d9' }}>
                                        <iframe src={item.pdf_url} style={{ width: '100%', height: '300px', border: 'none', display: 'block', background: 'white' }} title="Document officiel" />
                                    </div>
                                )}

                                {(item.closing || item.signature_name || item.signature_title) && (
                                    <div style={{ textAlign: 'right', marginBottom: '30px' }}>
                                        {item.closing && (
                                            <p style={{ fontSize: '14px', fontWeight: 500, color: '#0e1013', margin: '0 0 18px' }}>{item.closing}</p>
                                        )}
                                        {item.signature_name && (
                                            <p style={{ fontSize: '14px', fontWeight: 700, color: '#0e1013', margin: '0 0 2px' }}>{item.signature_name}</p>
                                        )}
                                        {item.signature_title && (
                                            <p style={{ fontSize: '12px', fontWeight: 500, color: '#6b6459', margin: 0 }}>{item.signature_title}</p>
                                        )}
                                    </div>
                                )}

                                <div style={{ width: '100%', height: '1px', background: '#e5e2d9', marginBottom: '18px' }} />

                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
                                    <p style={{ fontSize: '10px', fontWeight: 500, color: '#a19c8f', margin: 0, letterSpacing: '0.03em' }}>
                                        {t.footer}
                                    </p>
                                    {(item.link_url || item.pdf_url) && (
                                        <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
                                            {item.link_url && (
                                                <span onClick={() => navigate(item.link_url)} className="wlink" style={{ fontSize: '12px', fontWeight: 700, color: '#0e1013', letterSpacing: '0.04em', textTransform: 'uppercase', cursor: 'pointer' }}>
                                                    {t.view}
                                                </span>
                                            )}
                                            {item.pdf_url && (
                                                <a href={item.pdf_url} target="_blank" rel="noreferrer" className="wlink" style={{ fontSize: '12px', fontWeight: 500, color: '#6b6459', letterSpacing: '0.04em', textTransform: 'uppercase', textDecoration: 'none' }}>
                                                    {t.pdf}
                                                </a>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <Footer variant="light" />
        </div>
    );
}

export default Communiques;